import { DataTable, TextCell, useDataTableUsingState } from '@juvare/common-data-table-ui/DataTable';
import { FiltersBar, useFilterConfig } from '@juvare/common-data-table-ui/FiltersBar';
import { accountEntityName } from '@juvare/contact-management/backend/features/accounts/shared';
import { useStorageList } from '@juvare/platform-sdk-react/ui/hooks';
import { laggy } from '@juvare/platform-sdk-react/ui/utils';
import { Box, Tab, Tabs } from '@mui/material';
import React from 'react';
import { useAlertUiTranslation } from '../../../../../hooks';
import { usePlatformFunction } from '../../../../../hooks/usePlatformFunction';
export const SelectedRecipientsDialogContent = (props) => {
    var _a, _b;
    const { t } = useAlertUiTranslation();
    const internalRecipients = useDataTableUsingState({
        useDataProvider: (request) => {
            const response = useStorageList(accountEntityName, props.accountFilter
                ? {
                    filter: [
                        ...props.accountFilter,
                        ...(request.filterData.search
                            ? [
                                {
                                    column: 'search',
                                    op: 'full-text-query',
                                    value: request.filterData.search,
                                },
                            ]
                            : []),
                    ],
                    orderBy: [{ column: request.sortColumnKey, desc: request.sortColumnDesc }],
                    limit: request.limit,
                    offset: request.offset,
                }
                : undefined);
            return {
                data: response.data
                    ? {
                        rows: response.data.rows,
                        filteredRowCount: response.data.total,
                    }
                    : undefined,
                isLoading: response.isLoading,
                error: response.error,
                reload: response.mutate,
            };
        },
        defaultSortColumnKey: 'displayName',
        defaultRowsPerPage: 10,
    });
    const internalRecipientsFilterConfig = useFilterConfig((builder) => {
        builder.quickSearchFilter({ label: t('common.search'), dataPath: 'search', sx: { maxWidth: 340 } });
    }, [t]);
    const externalRecipients = useDataTableUsingState({
        useDataProvider: (request) => {
            var _a, _b, _c, _d, _e;
            const response = usePlatformFunction('listExternalRecipients', props.importId
                ? {
                    importId: props.importId,
                    listRequest: {
                        filter: request.filterData.search
                            ? [{ column: 'search', op: 'full-text-query', value: request.filterData.search }]
                            : undefined,
                        orderBy: [{ column: request.sortColumnKey, desc: request.sortColumnDesc }],
                        limit: request.limit,
                        offset: request.offset,
                    },
                }
                : undefined, undefined, undefined, {
                use: [laggy],
                revalidateOnFocus: false,
            });
            return {
                data: response.data
                    ? {
                        rows: (_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.result) === null || _b === void 0 ? void 0 : _b.rows,
                        filteredRowCount: (_e = (_d = (_c = response.data) === null || _c === void 0 ? void 0 : _c.result) === null || _d === void 0 ? void 0 : _d.total) !== null && _e !== void 0 ? _e : 0,
                    }
                    : undefined,
                isLoading: response.isLoading,
                error: response.error,
                reload: response.mutate,
            };
        },
        defaultSortColumnKey: 'displayName',
        defaultRowsPerPage: 10,
    });
    const externalRecipientsFilterConfig = useFilterConfig((builder) => {
        builder.quickSearchFilter({ label: t('common.search'), dataPath: 'search', sx: { maxWidth: 340 } });
    }, [t]);
    const emails = ((_b = (_a = props.emailCc) === null || _a === void 0 ? void 0 : _a.split(',')) !== null && _b !== void 0 ? _b : []).map((email, index) => ({ id: '' + index, email }));
    const emailsDataTable = useDataTableUsingState({
        useDataProvider: (request) => {
            var _a;
            let list = [...emails];
            const trimmedQuery = (_a = request.filterData.search) === null || _a === void 0 ? void 0 : _a.trim().toUpperCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
            if (trimmedQuery) {
                list = list.filter((item) => item.email
                    .trim()
                    .toUpperCase()
                    .normalize('NFKD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .includes(trimmedQuery));
            }
            if (request.sortColumnDesc) {
                list.reverse();
            }
            return {
                data: {
                    rows: list.slice(request.offset, request.offset + request.limit),
                    filteredRowCount: list.length,
                },
                reload: () => undefined,
            };
        },
        defaultSortColumnKey: 'email',
        defaultRowsPerPage: 10,
    });
    const emailsFilterConfig = useFilterConfig((builder) => {
        builder.quickSearchFilter({ label: t('common.search'), dataPath: 'search', sx: { maxWidth: 340 } });
    }, [t]);
    let initialTab = 0;
    if (props.accountFilter) {
        initialTab = 1;
    }
    else if (props.importId) {
        initialTab = 2;
    }
    else if (props.emailCc) {
        initialTab = 3;
    }
    const [tab, setTab] = React.useState(initialTab);
    return (React.createElement(React.Fragment, null,
        React.createElement(Box, { mb: 2 },
            React.createElement(Tabs, { value: tab, onChange: (_event, value) => setTab(value) },
                props.accountFilter && React.createElement(Tab, { label: t('common.internalRecipients'), value: 1 }),
                props.importId && React.createElement(Tab, { label: t('common.externalRecipients'), value: 2 }),
                props.emailCc && React.createElement(Tab, { label: t('common.cc'), value: 3 }))),
        tab === 1 && (React.createElement(DataTable, Object.assign({}, internalRecipients.dataTableProps, { columns: [
                {
                    headerLabel: t('common.name'),
                    sortKey: 'displayName',
                    cellFunction: (row) => React.createElement(TextCell, null, row.columns.displayName),
                },
            ], filtersBar: React.createElement(FiltersBar, Object.assign({}, internalRecipients.filtersBarProps, { config: internalRecipientsFilterConfig })) }))),
        tab === 2 && (React.createElement(DataTable, Object.assign({}, externalRecipients.dataTableProps, { columns: [
                {
                    headerLabel: t('common.name'),
                    sortKey: 'displayName',
                    cellFunction: (row) => React.createElement(TextCell, null, row.columns.displayName),
                },
            ], filtersBar: React.createElement(FiltersBar, Object.assign({}, externalRecipients.filtersBarProps, { config: externalRecipientsFilterConfig })) }))),
        tab === 3 && (React.createElement(DataTable, Object.assign({}, emailsDataTable.dataTableProps, { columns: [
                {
                    headerLabel: t('common.email'),
                    sortKey: 'email',
                    cellFunction: (row) => React.createElement(TextCell, null, row.email),
                },
            ], filtersBar: React.createElement(FiltersBar, Object.assign({}, emailsDataTable.filtersBarProps, { config: emailsFilterConfig })) })))));
};
